import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createQuestionCategory,
  deleteQuestionCategory,
  fetchQuestionCategories,
  fetchQuestionCategorySummary,
  generateQuestionCategories,
  updateQuestionCategory,
} from 'services/backend/questionCategories';
import { ReactQueryFunctionCallbacks } from 'types/internal';

export const questionCategoryQueries = {
  all: () => ['questionCategories'],
  byQuestion: (opts: { questionId: number; waveId?: number }) =>
    queryOptions({
      queryFn: () =>
        fetchQuestionCategories({
          questionId: opts.questionId,
          waveId: opts.waveId,
        }),
      queryKey: [
        ...questionCategoryQueries.all(),
        'question',
        opts.questionId,
        opts.waveId ?? 'all',
      ],
    }),
  summary: (opts: { questionId: number; waveId?: number }) =>
    queryOptions({
      queryFn: () =>
        fetchQuestionCategorySummary({
          questionId: opts.questionId,
          waveId: opts.waveId,
        }),
      queryKey: [
        ...questionCategoryQueries.all(),
        'summary',
        opts.questionId,
        opts.waveId ?? 'all',
      ],
    }),
};

export function useCreateQuestionCategory(
  opts?: ReactQueryFunctionCallbacks<typeof createQuestionCategory>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createQuestionCategory,
    onSuccess: async (data, variables, context) => {
      const { questionId, waveId } = variables;
      await queryClient.invalidateQueries({
        queryKey: [
          ...questionCategoryQueries.all(),
          'question',
          questionId,
          waveId ?? 'all',
        ],
      });

      opts?.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateQuestionCategory(
  opts?: ReactQueryFunctionCallbacks<typeof updateQuestionCategory>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: updateQuestionCategory,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: questionCategoryQueries.all(),
      });

      opts?.onSuccess?.(data, variables, context);
    },
  });
}

export function useDeleteQuestionCategory(
  opts?: ReactQueryFunctionCallbacks<typeof deleteQuestionCategory>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: deleteQuestionCategory,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: questionCategoryQueries.all(),
      });

      opts?.onSuccess?.(data, variables, context);
    },
  });
}

export function useGenerateQuestionCategories(
  opts?: ReactQueryFunctionCallbacks<typeof generateQuestionCategories>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: generateQuestionCategories,
    onSuccess: async (data, variables, context) => {
      const { questionId, waveId } = variables;
      await queryClient.invalidateQueries({
        queryKey: [
          ...questionCategoryQueries.all(),
          'question',
          questionId,
          waveId ?? 'all',
        ],
      });

      opts?.onSuccess?.(data, variables, context);
    },
  });
}
